import { isCmajSolicitationType, normalizeSolicitationType, SolicitationType } from "@/static/Solicitation"
import type { IDestinationProvider, IDestinationProviderLocalDiffuser } from "@/types/DestinationProvider.types"
import type { ISolicitationItem } from "@/types/Solicitation.types"

function isLinkedToTic(
  localDiffuser: IDestinationProviderLocalDiffuser,
  ticId: number | null
): boolean {
  if (localDiffuser.external_id === null) {
    return false
  }

  if (ticId === null) {
    return true
  }

  return Number(localDiffuser.tourism_information_center?.id) === Number(ticId)
}

function externalIdsOf(solicitation: ISolicitationItem): number[] {
  return [
    ...new Set(
      [solicitation.provider_external_id, solicitation.provider?.id]
        .filter((value): value is number => value != null)
        .map(Number)
    ),
  ]
}

function solicitationUsesTicLocalDiffuserOnFiche(
  solicitation: ISolicitationItem,
  destinationProvider: IDestinationProvider,
  ticId: number | null
): boolean {
  const solicitationExternalIds = externalIdsOf(solicitation)

  if (solicitationExternalIds.length === 0) {
    return true
  }

  return (destinationProvider.local_diffusers ?? []).some(
    (localDiffuser) =>
      isLinkedToTic(localDiffuser, ticId) &&
      solicitationExternalIds.includes(Number(localDiffuser.external_id))
  )
}

/**
 * Ids Hubo (pro-connexion) des demandes émises par l'office de tourisme courant.
 * Toutes les liaisons vers l'OT comptent, pas seulement le sit_owner : une
 * sollicitation peut concerner une fiche dont un autre OT est propriétaire SIT.
 */
export function getDialogueSolicitationExternalIds(
  destinationProviders: IDestinationProvider[],
  ticId: number | null
): number[] {
  const externalIds = new Set<number>()

  destinationProviders.forEach((destinationProvider) => {
    ;(destinationProvider.local_diffusers ?? []).forEach((localDiffuser) => {
      if (!isLinkedToTic(localDiffuser, ticId)) {
        return
      }

      externalIds.add(Number(localDiffuser.external_id))
    })
  })

  return [...externalIds]
}

function namesOf(solicitation: ISolicitationItem): string[] {
  return [
    ...new Set(
      [solicitation.title, solicitation.account_name]
        .map((value) => value?.trim().toLowerCase())
        .filter((value): value is string => Boolean(value))
    ),
  ]
}

function findByTicExternalId(
  destinationProviders: IDestinationProvider[],
  ticId: number | null,
  externalId: number
): IDestinationProvider | undefined {
  return destinationProviders.find((destinationProvider) =>
    (destinationProvider.local_diffusers ?? []).some(
      (localDiffuser) =>
        isLinkedToTic(localDiffuser, ticId) && Number(localDiffuser.external_id) === Number(externalId)
    )
  )
}

function findUniqueByName(
  destinationProviders: IDestinationProvider[],
  names: string[]
): IDestinationProvider | null {
  for (const name of names) {
    const matches = destinationProviders.filter(
      (destinationProvider) => destinationProvider.name.trim().toLowerCase() === name
    )

    if (matches.length === 1) {
      return matches[0]
    }
  }

  return null
}

/**
 * Relie une sollicitation à une fiche accessible à l'OT courant.
 * Le filtrage par compte OT est effectué côté API (dialogue-solicitations).
 */
export function findDialogueDestinationProviderForSolicitation(
  solicitation: ISolicitationItem,
  destinationProviders: IDestinationProvider[],
  ticId: number | null
): IDestinationProvider | null {
  const names = namesOf(solicitation)
  const byName = findUniqueByName(destinationProviders, names)

  if (byName && solicitationUsesTicLocalDiffuserOnFiche(solicitation, byName, ticId)) {
    return byName
  }

  const hubProviderId = solicitation.provider?.id
  if (hubProviderId != null) {
    const byHubProvider = findByTicExternalId(destinationProviders, ticId, hubProviderId)

    if (byHubProvider) {
      return byHubProvider
    }
  }

  const taggedId = solicitation.provider_external_id
  if (taggedId == null) {
    return null
  }

  const solicitationType = normalizeSolicitationType(solicitation.type)
  const establishmentTitle = solicitation.title?.trim()
  if (
    solicitationType !== SolicitationType.INDIV &&
    solicitationType !== SolicitationType.CADH &&
    establishmentTitle
  ) {
    return null
  }

  return findByTicExternalId(destinationProviders, ticId, taggedId) ?? null
}

/**
 * Une sollicitation est affichée dans Mes échanges si elle est rattachée à une fiche
 * de l'OT courant, ou s'il s'agit d'une MAJ / formulaire individuel renvoyé par
 * dialogue-solicitations (sans fiche associée).
 */
export function isDialogueSolicitationVisibleForTic(
  solicitation: ISolicitationItem,
  destinationProviders: IDestinationProvider[],
  ticId: number | null
): boolean {
  if (findDialogueDestinationProviderForSolicitation(solicitation, destinationProviders, ticId) !== null) {
    return true
  }

  if (isCmajSolicitationType(solicitation.type)) {
    return true
  }

  const normalizedType = normalizeSolicitationType(solicitation.type)

  return normalizedType !== SolicitationType.DEFAULT && Boolean(solicitation.token?.trim())
}
